import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@/src/contexts/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const getDeviceFingerprint = (): string => {
  try {
    const nav = navigator as any;
    const navigatorInfo = navigator.userAgent + navigator.language + (nav.hardwareConcurrency || '') + (nav.deviceMemory || '');
    const screenInfo = window.screen.width + 'x' + window.screen.height + 'x' + window.screen.colorDepth;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return btoa(unescape(encodeURIComponent(navigatorInfo + screenInfo + tz))).substring(0, 32);
  } catch (e) {
    return 'fallback-fingerprint-' + Math.random().toString(36).substring(2, 10);
  }
};

const LegalAcknowledgmentModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const { recordLegalAcknowledgment } = useAuth();
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!isChecked) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const fingerprint = getDeviceFingerprint();
      await recordLegalAcknowledgment(fingerprint, '1.0.0');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (e) {
      console.error('Failed to submit legal acknowledgment:', e);
      setError('An error occurred while saving your consent. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="legal-acknowledgment-modal-wrapper" className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {}} // Block clicking outside to close
            className="fixed inset-0 bg-black/75"
          />

          <motion.div
            initial={{ scale: 0.95, y: 15, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 15, opacity: 0 }}
            className="relative w-full max-w-lg bg-[#3a3430] border border-white/[0.06] rounded-xl text-white overflow-hidden max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col"
          >
            {/* Top Border Accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#F26522] z-10" />

            <div className="flex-1 overflow-y-auto p-8 sm:p-10 custom-scrollbar">
              <div className="space-y-6 sm:space-y-8">
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#F26522] font-heading">
                    <i className="fas fa-shield-halved text-[#F26522]"></i>
                    <span>Verification Required</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight font-heading text-white">
                    Before you proceed.
                  </h2>
                </div>

                <div className="space-y-4">
                  <p className="text-base font-medium leading-relaxed text-white font-heading border-l-2 border-[#F26522] pl-4">
                    eNvesta AI helps you plan. It does not decide for you.
                  </p>

                  <div className="text-[11px] text-white/60 leading-relaxed font-sans space-y-3 bg-[#322D29] p-4 border border-white/[0.06] rounded-lg">
                    <p>
                      All strategic structures, capital schedules, financial projections, and risk guard analyses are algorithmic outputs derived from generalized dataset aggregates. They are mathematical modeling tools, not certified representations of legal, tax, financial, or brokerage compliance.
                    </p>
                    <p>
                      By activating this intelligence layer, you formalize acknowledgment of advisory boundaries and authorize the capture of consent metadata (timestamp, device authentication attributes).
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 text-red-400 text-[11px] font-medium border border-red-500/20 rounded-lg">
                    {error}
                  </div>
                )}

                <div className="space-y-6">
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <div className="relative flex items-center mt-0.5">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                        disabled={isSubmitting}
                        className="peer h-5 w-5 cursor-pointer appearance-none border border-white/20 bg-[#322D29] checked:border-[#F26522] checked:bg-[#F26522] focus:outline-none transition-all rounded"
                      />
                      <i className="fas fa-check absolute opacity-0 peer-checked:opacity-100 text-white text-xs left-1 pointer-events-none transition-all"></i>
                    </div>
                    <span className="text-[12px] font-normal leading-relaxed text-white/80 select-none group-hover:text-white transition-colors">
                      I understand eNvesta AI is not a financial advisor, investment adviser, or broker, and I will consult qualified professionals before making business decisions.
                    </span>
                  </label>

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      onClick={handleConfirm}
                      disabled={!isChecked || isSubmitting}
                      className={`flex-1 py-3.5 text-center text-[11px] font-bold uppercase tracking-widest transition-all font-heading truncate rounded-lg cursor-pointer ${
                        isChecked && !isSubmitting
                          ? 'bg-[#F26522] hover:bg-[#F26522]/90 text-white'
                          : 'bg-white/10 text-white/40 border border-white/5 cursor-not-allowed'
                      }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-transparent rounded-full animate-spin"></span>
                          <span>Saving Signature...</span>
                        </span>
                      ) : (
                        'Authorize & Proceed'
                      )}
                    </button>
                    <button
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="px-6 py-3.5 text-center text-[11px] font-bold uppercase tracking-widest text-white/60 hover:text-white transition-colors font-heading border border-white/10 hover:bg-white/5 rounded-lg cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LegalAcknowledgmentModal;
